import { DragEvent, RefObject, useRef } from 'react';
import { Column } from '@uikit/Tables/types';

interface UseColumnDragResult {
  getHeaderDragProps: (
    column: Column,
    index: number
  ) => {
    draggable: boolean;
    onDragStart: (e: DragEvent<HTMLTableCellElement>) => void;
    onDragOver: (e: DragEvent<HTMLTableCellElement>) => void;
    onDrop: (e: DragEvent<HTMLTableCellElement>) => void;
    onDragEnd: (e: DragEvent<HTMLTableCellElement>) => void;
    onDragLeave: (e: DragEvent<HTMLTableCellElement>) => void;
  };
}

export const useColumnDrag = (
  columns: Column[],
  setColumns: (cols: Column[]) => void,
  tableRef: RefObject<HTMLTableElement>
): UseColumnDragResult => {
  const srcIdx = useRef<number | null>(null);

  const isLocked = (col: Column) => col.isLocked;

  const clearIndicators = () =>
    tableRef.current
      ?.querySelectorAll<HTMLTableCellElement>('th[data-drop-side]')
      .forEach((el) => delete el.dataset.dropSide);

  const getHeaderDragProps = (col: Column, idx: number) => ({
    draggable: !isLocked(col),

    onDragStart: (e: DragEvent<HTMLTableCellElement>) => {
      if (isLocked(col)) {
        e.preventDefault();
        return;
      }
      srcIdx.current = idx;
      e.dataTransfer.effectAllowed = 'move';
      e.stopPropagation();
    },

    onDragOver: (e: DragEvent<HTMLTableCellElement>) => {
      if (srcIdx.current === null || srcIdx.current === idx || isLocked(col))
        return;
      e.preventDefault();
      e.stopPropagation();
      const rect = e.currentTarget.getBoundingClientRect();
      e.currentTarget.dataset.dropSide =
        e.clientX < rect.left + rect.width / 2 ? 'left' : 'right';
    },

    onDrop: (e: DragEvent<HTMLTableCellElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const src = srcIdx.current;
      srcIdx.current = null;
      clearIndicators();
      if (src === null || src === idx || isLocked(col)) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const dropOnRight = e.clientX >= rect.left + rect.width / 2;

      const next = [...columns];
      const [moved] = next.splice(src, 1);

      const adjustedIdx = idx > src ? idx - 1 : idx;

      const insertAt = dropOnRight ? adjustedIdx + 1 : adjustedIdx;

      next.splice(Math.max(0, Math.min(next.length, insertAt)), 0, moved);
      setColumns(next);
    },

    onDragEnd: () => {
      srcIdx.current = null;
      clearIndicators();
    },
    onDragLeave: (e: DragEvent<HTMLTableCellElement>) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
        delete e.currentTarget.dataset.dropSide;
      }
    },
  });

  return { getHeaderDragProps };
};
